import {
  CLOVER_FEE_OVERRIDE_KEYS,
  CLOVER_SECURITY_AND_TRANSARMOR_FIELDS,
  FEE_OVERRIDE_FIELDS,
  formatFee,
  type PricingTier,
} from "@/lib/pricingTiers";
import type {
  CloverSecurityAndTransarmorForm,
  FeeOverridesForm,
} from "./types";
import { TF } from "./TF";
import { FieldErrorText } from "./FormField";

export function CloverSecurityAndTransarmorSection({
  idPrefix,
  tier,
  cloverSecurityAndTransarmor,
  onCloverSecurityAndTransarmorChange,
  feeOverrides,
  onFeeOverrideChange,
  feeIdPrefix,
  errorAt,
}: {
  idPrefix: string;
  tier: PricingTier | null;
  cloverSecurityAndTransarmor: CloverSecurityAndTransarmorForm;
  onCloverSecurityAndTransarmorChange: (
    key: keyof CloverSecurityAndTransarmorForm,
    v: string,
  ) => void;
  // The Clover platform / security fees live in the fees object and use the
  // fee-override path, but render here (Susan's request) instead of the
  // Manual fee adjustment section. feeIdPrefix matches that section's ids
  // (e.g. "fee-override") so validation error lookups line up.
  feeOverrides: FeeOverridesForm;
  onFeeOverrideChange: (key: keyof FeeOverridesForm, v: string) => void;
  feeIdPrefix: string;
  errorAt?: (field: string) => string | undefined;
}) {
  return (
    <div className="space-y-3 rounded-md border border-dashed border-brand-border/70 bg-brand-surface/30 p-3">
      <div>
        <p className="text-sm font-medium text-brand-text">
          Clover Security + TransArmor{" "}
          <span className="font-normal text-brand-muted">(admin only)</span>
        </p>
        <p className="mt-0.5 text-xs text-brand-muted">
          Overrides for the Clover Security + TransArmor block sent alongside
          the merchant. Leave any field blank to keep the tier default. Never
          shown to the merchant.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEE_OVERRIDE_FIELDS.filter((field) =>
          CLOVER_FEE_OVERRIDE_KEYS.includes(
            field.key as (typeof CLOVER_FEE_OVERRIDE_KEYS)[number],
          ),
        ).map((field) => {
          const id = `${feeIdPrefix}.${field.key}`;
          const key = field.key as keyof FeeOverridesForm;
          const tierDefault = tier?.fees[field.key];
          return (
            <TF
              key={field.key}
              id={id}
              label={field.label}
              inputMode="decimal"
              numeric="currency"
              value={feeOverrides[key]}
              onChange={(v) => onFeeOverrideChange(key, v)}
              placeholder={
                tierDefault == null
                  ? "Tier default"
                  : formatFee(tierDefault as number)
              }
              error={errorAt?.(id)}
            />
          );
        })}
        {CLOVER_SECURITY_AND_TRANSARMOR_FIELDS.map((field) => {
          const id = `${idPrefix}.${field.key}`;
          const key = field.key as keyof CloverSecurityAndTransarmorForm;
          const value = cloverSecurityAndTransarmor[key];
          const tierValue = tier?.cloverSecurityAndTransarmor[field.key];

          if (field.kind === "flag") {
            return (
              <div key={field.key}>
                <label htmlFor={id} className="label mb-1.5 block">
                  {field.label}
                </label>
                <select
                  id={id}
                  className="input"
                  value={value}
                  onChange={(e) =>
                    onCloverSecurityAndTransarmorChange(key, e.target.value)
                  }
                >
                  <option value="">
                    {typeof tierValue === "boolean"
                      ? `${tierValue ? "On" : "Off"} (Tier Default)`
                      : "Tier Default"}
                  </option>
                  <option value="true">Override: On</option>
                  <option value="false">Override: Off</option>
                </select>
              </div>
            );
          }

          if (field.kind === "select") {
            // If a previously-saved value is not in the known option list,
            // keep it selectable so an edit never silently drops it.
            const options = field.options ?? [];
            const showsUnknown = value !== "" && !options.includes(value);
            const selectError = errorAt?.(id);
            return (
              <div key={field.key}>
                <label htmlFor={id} className="label mb-1.5 block">
                  {field.label}
                </label>
                <select
                  id={id}
                  className="input"
                  value={value}
                  onChange={(e) =>
                    onCloverSecurityAndTransarmorChange(key, e.target.value)
                  }
                  aria-invalid={!!selectError}
                  aria-describedby={selectError ? `${id}-err` : undefined}
                >
                  <option value="">
                    {field.emptyLabel ??
                      (tierValue == null
                        ? "Off (Tier Default)"
                        : `${String(tierValue)} (Tier Default)`)}
                  </option>
                  {options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                  {showsUnknown ? <option value={value}>{value}</option> : null}
                </select>
                {selectError && (
                  <FieldErrorText id={`${id}-err`}>{selectError}</FieldErrorText>
                )}
              </div>
            );
          }

          const placeholder =
            tierValue == null
              ? "Tier default"
              : field.kind === "currency"
                ? formatFee(tierValue as number)
                : String(tierValue);
          return (
            <TF
              key={field.key}
              id={id}
              label={field.label}
              inputMode={
                field.kind === "currency" || field.kind === "number"
                  ? field.kind === "number"
                    ? "numeric"
                    : "decimal"
                  : "text"
              }
              numeric={
                field.kind === "currency"
                  ? "currency"
                  : field.kind === "number"
                    ? "integer"
                    : undefined
              }
              value={value}
              onChange={(v) => onCloverSecurityAndTransarmorChange(key, v)}
              maxLength={field.kind === "string" ? 100 : 12}
              placeholder={placeholder}
              error={errorAt?.(id)}
            />
          );
        })}
      </div>
    </div>
  );
}
